import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { Location, PlatformLocation, ViewportScroller } from '@angular/common';
import {
  AppBaseComponent,
  DataCollectionService,
  doArrayDirtyCheck,
  getDeepestRouteSnapshot,
  handleUnsavedArrays,
  Instance,
  InstanceResolveData,
  LoadingService,
  OverlayService,
  ViewType,
} from '@finra/rampdi-forms-common';
import { InstanceService, MU1WithSupportingData } from './services/instance.service';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  NavigationStart,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { BeastClientService, BeastEvent } from '@finra/beast-client';
import { CompletenessCheckService } from './services/completeness-check.service';
import { FORM_TYPES, NavigationDataService } from './services/navigation-data.service';
import { RedlineService } from './services/redline.service';
import { environment } from '../environments/environment';
import {
  distinctUntilChanged,
  catchError,
  concatMap,
  filter,
  finalize,
  map,
  pairwise,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { DisplayRulesService, FilingStatusDisplayRules, FILING_STATUS } from './services/display-rules.service';
import { ModUserProfile } from 'projects/app-nmls-entity-profile/src/app/models/profiles.response';
import { BehaviorSubject, firstValueFrom, forkJoin, from, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { HostApiService } from 'lib-host-interface';
import { toSignal } from '@angular/core/rxjs-interop';
import { SharedStateService } from './services/shared-state.service';
import { ExternalService } from '@finra/angular-data-collection-sdk';
import { GlobalContext } from './services/global-context.service';

export const BEAST_EVENTS: {
  [key: string]: {
    description: string;
    resource: string;
    action: string;
    recents?: any;
  };
} = {
  // TODO: mocking to test redlining, updater after BEAST integration
  'fv:redlining': {
    description: 'foo',
    action: 'bar',
    resource: 'what',
  },
  print: {
    description: 'Click on Print View from the footer',
    resource: 'FV:PrintForm',
    action: 'Click',
  },
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: {
    '(window:afterprint)': 'onAfterPrint()',
    '(window:beforeprint)': 'onBeforePrint()',
    '(window:resize)': 'getScreenSize($event)',
  },
})
export class AppComponent
  extends AppBaseComponent<
    MU1WithSupportingData,
    Instance<any>,
    InstanceResolveData<MU1WithSupportingData, Instance<any>>
  >
  implements OnInit, OnDestroy, AfterViewInit
{
  lastOpenedSectionDE = 'licenses';
  hostApi: HostApiService = inject(HostApiService);
  private externalRoutingEventsSub: Subscription;
  isInternalView: boolean = environment.identityProviderRealm === 'isso';
  hasRoutingError = false;
  concurrencyModal: boolean = false;
  migrationModal: boolean = false;
  @ViewChild('disclosureDeleteModal', { static: true }) disclosureDeleteModal: TemplateRef<any>;
  navrouteData: any;
  profile: ModUserProfile;
  isStandalone = !this.platformLocation.hostname.includes('gateway');
  displayRulesService = inject(DisplayRulesService);
  displayRules$: Observable<FilingStatusDisplayRules> = inject(DisplayRulesService).displayRulesSubj;
  private isDestroying$ = new Subject<void>();
  private phoneAutocompleteObserver: MutationObserver;
  hasReadOnlyMode$$ = toSignal(
    this.displayRules$.pipe(
      takeUntil(this.isDestroying$),
      map((res) => Object.keys(res)?.length > 0 && res['readOnlyMode']),
    ),
  );
  isMobileView: boolean = false;
  private window = window;
  loggedInUserName: any;
  subjectId: string = '';
  confirmationNumber: string;
  hasConcurrencyIssue: boolean = false;
  displayMigrationBanner: boolean = false;
  migrationInProgress: boolean = false;
  migrationInstanceId: string = '';
  isGuidance: boolean;
  formView: boolean;
  currentSelectDisclosure;
  selectedDisclosureIndex: number | null;
  modalDeletionReason: string = '';
  disclosureDeleteReasonError: string = '';
  inProgressNav: boolean;
  regulatorUser: boolean;
  isRegulatorUser: boolean = false;
  saveInProgress: boolean = false;

  protected get environment(): typeof environment {
    return environment;
  }
  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private navDataService: NavigationDataService,
    private instanceService: InstanceService,
    private loadingService: LoadingService,
    private OS: OverlayService,
    private location: Location,
    private cd: ChangeDetectorRef,
    private beast: BeastClientService,
    private DCS: DataCollectionService,
    private CC: CompletenessCheckService,
    private RL: RedlineService,
    private platformLocation: PlatformLocation,
    private VPS: ViewportScroller,
    public elementRef: ElementRef,
    private sharedStateService: SharedStateService,
    private ES: ExternalService,
    private DS: DisplayRulesService,
    private globalContext: GlobalContext,
    private ngZone: NgZone,
  ) {
    router.initialNavigation();
    super(route, router, cd, loadingService, OS, CC, RL, beast, location, instanceService, true);
    this.beastEvent = BEAST_EVENTS;
    // expose shouldWarnBeforeExit() directly on the web component instance
    (this.elementRef.nativeElement as any).shouldWarnBeforeExit = this.shouldWarnBeforeExit.bind(this);
    DS.isRegulatorUser.pipe(takeUntil(this.isDestroying$)).subscribe((res) => {
      this.isRegulatorUser = res;
    });
    // React to regulator user changes using effect

    // Make goToEmploymentHistory available globally
    (window as any).goToEmploymentHistory = () => {
      this.ngZone.run(() => {
        const currentUrl = this.router.url;
        const employmentLink = currentUrl.replace(/section\/[^/?]+/, 'section/employmentHistory');
        this.router.navigateByUrl(employmentLink);
      });
    };
  }

  private isProcessingSubject = new BehaviorSubject<boolean>(false);
  private errorMessageSubject = new BehaviorSubject<string>(null);

  // Expose as observables
  isProcessing$ = this.isProcessingSubject.asObservable();
  errorMessage$ = this.errorMessageSubject.asObservable();

  closeProcessedModal(event?) {
    if (event?.detail?.continue) {
      this.sharedStateService.setPostSubmission(true);
    }
  }

  onSubmissionConfirmationOk() {
    this.closeProcessedModal({ detail: { continue: true } });
  }

  getInitialViewBeastEvent(navEvent) {
    const instance = this.getDeepestRouteInst();
    const filingTypeCode = instance?.instanceMetadata?.attributes?.filingTypeCode;
    const id = instance?.id;

    const eventConfig = this.beastEvent[`view:${filingTypeCode}`];
    const be: BeastEvent = {
      action: eventConfig?.action,
      resource: eventConfig?.resource,
      correlationId: id,
      details: {
        ...eventConfig?.recents,
        description: eventConfig?.description,
        url: navEvent.url,
        urlAfterRedirects: navEvent.urlAfterRedirects,
        recentsLabel: `${instance?.templateName?.toUpperCase()} id`,
      },
    };

    return be;
  }

  ngOnInit(): void {
    this.getScreenSize();
    // Handle Chrome autocomplete adding +1 country code to phone fields
    this.setupPhoneAutocompleteHandler();
    this.navData$ = this.router.events.pipe(
      tap((navEvent) => {
        this.tapNavEvent(navEvent);
      }),
      filter((event) => event instanceof RoutesRecognized || event instanceof NavigationEnd),
      distinctUntilChanged(),
      pairwise(),
      filter(([e1, e2]) => e1 instanceof RoutesRecognized && e2 instanceof NavigationEnd),
      withLatestFrom(this.CC.completenessCheck$),
      map(([[e1, e2], cc]) => {
        return this.mapNavEvent(cc);
      }),
    );
    this.hostApi.routing?.events?.pipe(takeUntil(this.isDestroying$)).subscribe((event) => {
      console.log(`app-mu1-form received app host routing event`, event);
      if (event && event.type === 'start' && event.component === 'finra-mu1-form' && event.url) {
        // make sure the event is targeting my MFE
        console.log(`app-mu1-form warn for ${event.url}`);
      }
    });

    this.CC.openConcurrencyModal$.pipe(takeUntil(this.isDestroying$)).subscribe((openConcurrencyModal: boolean) => {
      this.concurrencyModal = openConcurrencyModal;
    });

    this.DS.navigationError.pipe(takeUntil(this.isDestroying$)).subscribe((res) => {
      if (res) {
        this.hasRoutingError = res;
      }
    });
    this.instanceService.concurrencyDetected$.pipe(takeUntil(this.isDestroying$)).subscribe(() => {
      this.hasConcurrencyIssue = true;
      this.concurrencyModal = true;
      this.cd.detectChanges();
    });
  }

  navigateToMenu(detail) {
    this.navigateToSection(detail);
  }

  getSnapShot() {
    return getDeepestRouteSnapshot(this.route)?.data;
  }

  getDeepestRouteInst() {
    return getDeepestRouteSnapshot(this.route).data?.instanceData?.instance;
  }

  getIsDraft() {
    return this.isDraft;
  }

  getViewType() {
    return this.viewType;
  }

  onValidate() {
    if (!this.getIsDraft()) {
      console.log('Skipped validating. Instance is not a draft');
      return;
    }
    this.CC.setCompletenessCheckData({ open: true, loading: true });

    //Validate method is default rampdi except for this logic to skip saving on readonly mode.
    if (this.hasReadOnlyMode$$()) {
      console.log('Read only mode validation, skip saving.');
      this.CC.validateInstance(this.instanceService.currentInstance)
        .pipe(
          map((res: any) => {
            if (res.success || res?.data?.status === 400) {
              this.CC.completenessCheckOthersMessage(res);
            } else {
              this.CC.setCompletenessCheckData({ open: true, fail: true });
            }
            return;
          }),
        )
        .toPromise();
      return;
    }

    this.onSave({
      section: this.sectionNavData.id,
      view: this.getViewType(),
      state: {
        saveInstance: true,
        notify: false,
        beastNavEventKey: 'cc',
      },
    })
      .catch((e) => {
        console.warn('Problem when saving before completeness check');
        return 'cancelCC';
      })
      .then((cancelCC) => {});
  }

  onContinue() {
    // Clear the concurrency diff flag from sessionStorage when user clicks CONTINUE
    sessionStorage.removeItem('concurrencyDiff');

    const currentInstance = this.instanceService.currentInstance;
    const filingStatus = currentInstance?.instanceMetadata?.attributes?.filingStatus?.current;

    // Apply any pending data changes to the instance before saving
    Promise.all(this.getHandleUnsavedArrays())
      .then((data) => {
        if (data.every((a: any) => a?.continueSaving)) {
          this.instanceService.saveInProgress = true;
        }
        // Clear hasConcurrencyIssue flag and save
        currentInstance.instanceMetadata.attributes['hasConcurrencyIssue'] = false;
        currentInstance.instanceMetadata.attributes['hasUnprocessedConcurrentFiling'] = false;
        currentInstance.instanceMetadata.attributes['baselineVersion'] = currentInstance.instanceVersion + 1;
        this.instanceService.clearingConcurrencyFlag = true;
        return firstValueFrom(this.instanceService.saveInstance(currentInstance.id, currentInstance));
      })
      .then(() => {
        this.instanceService.clearingConcurrencyFlag = false;
        this.instanceService.saveInProgress = false;
        this.concurrencyModal = false;

        // If filing was Attested, only recall to Draft when concurrency is triggered by another filing submition
        const concurrencySections: string[] = currentInstance?.instanceMetadata?.attributes?.concurrencySections || [];
        if (filingStatus === FILING_STATUS.ATTESTED && concurrencySections.includes('FORM')) {
          globalThis.finformUnsavedChanges = false;
          return firstValueFrom(this.instanceService.recallFiling());
        }
      })
      .then(() => {
        globalThis.finformUnsavedChanges = false;
        this.window.location.reload();
      })
      .catch((error) => {
        this.instanceService.clearingConcurrencyFlag = false;
        this.instanceService.saveInProgress = false;
        console.error('Error during continue:', error);
      });
  }

  onMigrationContinue() {
    this.migrationInProgress = true;
    this.migrationModal = false;
    this.instanceService.currentInstance.instanceMetadata.attributes['displayMigrationBanner'] = false;
    this.instanceService.saveInstance(this.instanceService.currentInstance.id, this.instanceService.currentInstance);
    this.onSave({
      state: {
        beastNavEventKey: 'save',
      },
    }).finally(() => {
      this.migrationInProgress = false;
    });
  }

  navigateTo() {
    // Set concurrency diff flag in sessionStorage when user clicks the first "here" link
    sessionStorage.setItem('concurrencyDiff', 'true');

    const url = this.router.serializeUrl(
      this.router.createUrlTree([`${environment.dxtBasePath}/forms/mu1/view/formView/section/all`], {
        queryParams: { redlineCheck: true },
        queryParamsHandling: 'merge',
      }),
    );
    window.open(url, '_blank');
  }

  tapNavEvent(navEvent) {
    this.VPS.scrollToPosition([0, 0]);
    const { extras } = this.router.getCurrentNavigation();
    const { saveInstance, reviewed, initialView, beastNavEventKey, hasRoutingError } = extras?.state || {};

    if (navEvent['url'] === '/') {
      this.router.navigate(['/error']);
    }

    if (navEvent instanceof NavigationStart) {
      if (beastNavEventKey) {
        const eventConfig = this.beastEvent[beastNavEventKey];
        if (eventConfig) {
          const be: BeastEvent = {
            action: eventConfig?.action,
            resource: eventConfig?.resource,
            correlationId: this.instanceService?.currentInstance?.id,
            details: {
              description: eventConfig?.description,
              url: navEvent.url,
            },
          };
          this.beast.postBeastEvent(be);
        } else {
          console.warn('No beastNavEventKey for:' + beastNavEventKey);
        }
      }
    }

    if (navEvent instanceof RoutesRecognized) {
      //If read only mode then prevent auto-save from triggering
      if (this.hasReadOnlyMode$$() && extras?.state?.autoSaveInstance) {
        extras.state.autoSaveInstance = false;
      }
      if (saveInstance) {
        this.DCS.resetToDefault();
        this.loadingText = `Saving Filing Data...`;
      }
      if (navEvent.url === '/create') {
        this.loadingText = `Creating MU1 Form`;
      }
      this.loadingService.setLoading(true);
    }
    this.viewFilingTypeBeastEvent(navEvent, initialView, reviewed);
    this.hasRoutingError = hasRoutingError;
  }

  /**
   * Saves the current form instance and navigates to the specified section
   * @param {Object} options - Configuration options for the save operation
   * @param {string} [options.section] - Target section to navigate to after saving
   * @param {string} [options.view] - View type to navigate to after saving
   * @param {Object} options.state - Navigation state containing save configuration
   * @returns {Promise<boolean>} Promise resolving to navigation result or rejecting if save is skipped
   */
  onSave(options: { section?: string; view?: string; state: any }) {
    if (!this.isDraft) {
      console.log('Skipped saving. Instance is not a draft');
      return Promise.reject('skipping save');
    }
    return Promise.all(this.getHandleUnsavedArrays()).then((data) => {
      if (!data.every((a: any) => a?.continueSaving)) {
        console.log('Not saving', data);
        return Promise.reject('Not Saving');
      } else {
        console.log('Saving...');
        this.instanceService.saveInProgress = true;
        let { licenseTypeCode, sectionId, viewType } = getDeepestRouteSnapshot(this.route).params;
        if (options.section) {
          sectionId = options.section;
          licenseTypeCode = undefined;
        }
        const navParams: any = ['view', options.view || viewType, 'section', sectionId];
        licenseTypeCode && navParams.push({ licenseTypeCode });
        const defaultState = { saveInstance: true, notify: true };
        return this.router.navigate(navParams, {
          queryParamsHandling: 'merge',
          state: { ...defaultState, ...options?.state },
        });
      }
    });
  }

  getHandleUnsavedArrays() {
    return handleUnsavedArrays();
  }

  /**
   * Extracts and returns application data from the current route
   * @returns {Object} Object containing view type, filing type code, navigation ID, and form schemas
   * @returns {string} returns.viewType - The current view type
   * @returns {string} returns.filingTypeCode - The filing type code from instance metadata
   * @returns {string} returns.navId - The navigation ID constructed from section ID and license type
   * @returns {Object} returns.formSchemas - The form schemas from route data
   */
  getAppData() {
    const filingTypeCode = getDeepestRouteSnapshot(this.route).data.instanceData?.instance?.instanceMetadata?.attributes
      ?.filingTypeCode;
    const params = getDeepestRouteSnapshot(this.route).paramMap;
    const formSchemas = getDeepestRouteSnapshot(this.route).data.schemas;
    const sectionId = params?.get('sectionId');
    const licenseTypeCode = params?.get('licenseTypeCode');
    const viewType = params?.get('viewType') as ViewType;
    this.viewType = viewType;
    this.formView = this.viewType === 'formView';
    const navId = sectionId + (licenseTypeCode ? `;${licenseTypeCode}` : '');
    this.currentNavId = navId;

    return {
      viewType,
      filingTypeCode,
      navId,
      formSchemas,
    };
  }

  mapNavEvent(cc) {
    this.isGuidance = this.router.url.includes('guidance');
    const { viewType, navId, formSchemas, filingTypeCode } = this.getAppData();
    let instanceStatus;
    this.displayRulesService.displayRules$.subscribe((resp) => {
      instanceStatus = resp.displayedStatus;
    });

    const filingStatus = this.instanceService?.currentInstance?.instanceMetadata?.attributes?.filingStatus?.current;
    if (
      (filingStatus === FILING_STATUS.SUBMITTED || filingStatus === FILING_STATUS.PROCESSED) &&
      viewType !== 'formView'
    ) {
      const urlMatch = this.router.url.match(/section\/([^?]+)/);
      let sectionId = urlMatch ? urlMatch[1] : 'licenses';
      if (!sectionId.startsWith('formView')) {
        sectionId = 'formView' + sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
      }
      this.router.navigate(['view', 'formView', 'section', sectionId], {
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return [];
    }

    if (
      (cc['open'] ||
        (cc['open'] === false && navId === 'attestAndSubmit') ||
        (cc['close'] && navId === 'attestAndSubmit') ||
        (cc['open'] === undefined && navId === 'attestAndSubmit')) &&
      instanceStatus !== 'Filing Processed' &&
      instanceStatus !== 'Failed Processing'
    ) {
      this.CC.onValidateCCOpened();
    }
    const snapshotData = this.getSnapShot();
    this.subjectId = snapshotData?.instanceData?.initialInstance?.instanceMetadata?.target?.subjectId;
    const legalName =
      snapshotData?.instanceData?.initialInstance?.collectedData?.individualInformation?.personalInformation
        ?.individualName;
    this.hasConcurrencyIssue = this.instanceService?.currentInstance?.instanceMetadata.attributes?.hasConcurrencyIssue;
    this.displayMigrationBanner =
      this.instanceService?.currentInstance?.instanceMetadata.attributes?.displayMigrationBanner;
    this.migrationInstanceId = this.instanceService?.currentInstance?.instanceMetadata.attributes?.migrationInstanceId;
    let sectionText = this.navDataService.getNavData(`${viewType}.${filingTypeCode}`);
    sectionText[0].text = `${legalName?.firstName ? legalName?.firstName + ' ' : ''}${
      legalName?.middleName ? legalName?.middleName + ' ' : ''
    }${legalName?.lastName ? legalName?.lastName + ' ' : ''}${legalName?.suffix ? legalName?.suffix : ''}`;
    this.sectionNavData = this.navDataService.getNavItem(`${viewType}.${filingTypeCode}`, navId);
    this.title =
      (typeof formSchemas?.title === 'function' ? formSchemas?.title(this.sectionNavData) : formSchemas?.title) ??
      this.sectionNavData?.title ??
      '';

    if (
      !this.router.url.includes('/all') &&
      this.hasConcurrencyIssue &&
      this.viewType !== 'formView' &&
      !this.migrationInProgress &&
      !this.concurrencyModal
    ) {
      this.concurrencyModal = true;
    }
    if (this.displayMigrationBanner) {
      this.migrationModal = true;
    }
    this.instanceService.saveInProgress = false;
    if (snapshotData?.navData.length) {
      snapshotData.navData = snapshotData?.navData.map((data) => {
        if (data.type === 'header') {
          this.loggedInUserName = data.text;
          data['loggedInUserName'] = this.loggedInUserName;
          data.text = 'MU1 Form';
        }
        return data;
      });
    }
    const value = snapshotData?.navData;
    this.navrouteData = value;
    return [...snapshotData?.navData];
  }

  ngAfterViewInit(): void {
    this.CC.setDisclosureDeleteModal(this.disclosureDeleteModal);
    this.CC.setAppComponent(this);
  }

  setupPhoneAutocompleteHandler() {
    this.phoneAutocompleteObserver = new MutationObserver(() => {
      // Find finra-input-text elements with autocomplete="phone"
      const finraInputs = document.querySelectorAll('finra-input-text[autocomplete="phone"]');
      finraInputs.forEach((finraInput: any) => {
        if (finraInput.shadowRoot && !finraInput.dataset.phoneHandlerAdded) {
          finraInput.dataset.phoneHandlerAdded = 'true';
          const input = finraInput.shadowRoot.querySelector('input') as HTMLInputElement;
          if (input) {
            // Intercept the value setter to catch autocomplete before masking
            const originalValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!.set!;
            Object.defineProperty(input, 'value', {
              set: function (newValue) {
                const digits = String(this.value || newValue).replace(/\D/g, '');
                const mask = this.getAttribute('mask') || '';
                const maxLength = mask ? mask.replace(/[^0]/g, '').length : 10;
                if (digits.length > maxLength) {
                  originalValueSetter.call(this, digits.substring(digits.length - maxLength));
                  this.dispatchEvent(new Event('input', { bubbles: true }));
                } else {
                  originalValueSetter.call(this, newValue);
                }
              },
              get: function () {
                return Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!.get!.call(this);
              },
            });
          }
        }
      });
    });
    this.phoneAutocompleteObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['autocomplete'],
    });
  }

  ngOnDestroy() {
    this.phoneAutocompleteObserver?.disconnect();
    this.isDestroying$.next();
    this.isDestroying$.complete();
    this.externalRoutingEventsSub?.unsubscribe();
    this.instanceService.reset();
    delete (window as any).goToEmploymentHistory;
  }

  onPrint() {
    this.onBeforePrint();
    setTimeout(() => {
      globalThis.print();
    }, 50);
  }

  onBeforePrint() {
    this.isPrintMode = true;
    document.querySelectorAll('rampdi-legal-view-encrypted-value').forEach((element) => {
      element.dispatchEvent(new CustomEvent('encrypt'));
    });
  }

  onAfterPrint() {
    this.isPrintMode = false;
  }

  getScreenSize(event?) {
    if (window.innerWidth < 1140) {
      this.isMobileView = true;
    } else {
      this.isMobileView = false;
    }
  }

  public shouldWarnBeforeExit(): boolean {
    console.log('warn unsaved changes', globalThis?.finformUnsavedChanges);
    this.closeSnackbar(); //Clear snackbar when leaving micro-app
    return globalThis?.finformUnsavedChanges ? true : false;
  }

  closeSnackbar() {
    // Close any active snackbar directly instead of creating a new empty one
    if (this.OS.activeToastInstance) {
      this.OS.activeToastInstance.close();
    }
  }

  navigateToField($event) {
    const { item, section } = $event;
    const isOthersSectionWithNoNav = section.id === 'others';
    const isDisclosureExplanation = section.id === 'disclosureQuestions';
    const isCurrentSection = section.id === this.currentNavId;

    if (isDisclosureExplanation) {
      const selectors = this.CC.getSelectors(item, section);
      const formControlIndex = selectors[1] ? +selectors[1] : null;
      const model =
        this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures?.disclosureExplanations;
      const targetItem = model?.[formControlIndex];

      let targetSection;
      if (targetItem?.type === 'category') {
        targetSection = 'disclosureNoResponses';
      } else if (targetItem?.associations?.length > 0) {
        targetSection = this.getDisclosureSectionForQuestion(targetItem.associations[0]);
      }

      if (targetSection && targetSection !== this.currentNavId) {
        this.navigateToSection({ navItem: { id: targetSection } });
        setTimeout(() => {
          this.CC.gotoField(selectors);
        }, 2000);
        return;
      }
      this.CC.gotoField(selectors);
    } else if (isCurrentSection || isOthersSectionWithNoNav) {
      this.CC.gotoField(this.CC.getSelectors(item, section));
    } else {
      this.navigateToSection({ navItem: { id: section.id } });
      setTimeout(() => {
        this.CC.gotoField(this.CC.getSelectors(item, section));
      }, 2000);
    }
  }

  getDisclosureSectionForQuestion(questionId: string): string | undefined {
    const viewType = this.route.snapshot.paramMap.get('viewType') || 'dataEntry';
    const navData = this.navDataService.getNavData(`${viewType}.${FORM_TYPES.INITIAL}`);

    let disclosureSection;
    for (const item of navData || []) {
      if (item?.children) {
        disclosureSection = item.children.find(
          (child) => child.id === 'disclosureQuestions' || child.id === 'formViewDisclosureQuestions',
        );
        if (disclosureSection) break;
      }
    }

    if (disclosureSection?.children) {
      const testPath = `#/disclosureQuestions/disclosures/${questionId}`;
      for (const child of disclosureSection.children) {
        const pattern = child.pointerList?.[0];
        if (pattern instanceof RegExp && pattern.test(testPath)) {
          return child.id;
        }
      }
    }
    return undefined;
  }

  openInvoicePopup() {
    // Invoice not implemented for MU1 yet
  }

  closeInvoicePopup() {
    // Invoice not implemented for MU1 yet
  }

  /**
   * Navigates to a specified section with license type handling
   * @param {Object} e - Event object containing navigation item
   * @param {Object} state - Additional navigation state
   */
  navigateToSection(e, state = {}) {
    const hasUnsavedChanges = this.instanceService.hasUnsavedChanges();
    const isEditable = this.instanceService.isInstanceEditable(
      getDeepestRouteSnapshot(this.route).data.instanceData?.instance,
    );
    if (!e.navItem.skip) {
      if (e.navItem.id !== getDeepestRouteSnapshot(this.route).paramMap?.get('sectionId')) {
        let [id, licenseTypeCode] = e.navItem.id.split(';');
        const hasDirtyArrays = this.rampDoArrayDirtyCheck();
        const navParams = ['view', this.viewType, 'section', id];
        licenseTypeCode && navParams.push({ licenseTypeCode });
        const navExtras: NavigationExtras = {
          queryParams: { bannerType: null },
          queryParamsHandling: 'merge',
          state: {
            beastNavEventKey: `nav:${id}`,
            ...state,
          },
        };
        if (hasDirtyArrays) {
          this.OS.openStayLeaveModal()
            .afterClosed$.toPromise()
            .then((response) => {
              if (response.data) {
                this.router.navigate(navParams, {
                  ...navExtras,
                  state: { ...navExtras.state, autoSaveInstance: isEditable && hasUnsavedChanges },
                });
              } else {
                // to make sure the navData is still set for the current path
                this.router.navigateByUrl(this.router.routerState?.snapshot.url, navExtras);
              }
            });
        } else {
          console.log(`navigate: ${navParams}`, e);
          if (!this.inProgressNav) {
            this.inProgressNav = true;
            this.router
              .navigate(navParams, {
                ...navExtras,
                state: { ...navExtras.state, autoSaveInstance: isEditable && hasUnsavedChanges },
              })
              .then(() => {
                this.inProgressNav = false;
              });
          } else {
            console.log('nav in progress', this.inProgressNav);
          }
        }
      } else {
        // To Do
      }
    }
  }

  rampDoArrayDirtyCheck() {
    return doArrayDirtyCheck();
  }

  onToggleRedline(value) {
    if (value) {
      const eventConfig = this.beastEvent['fv:redlining'];
      const event: BeastEvent = {
        action: eventConfig?.action,
        resource: eventConfig?.resource,
        details: {
          description: eventConfig.description,
        },
        correlationId: this.instanceService.currentInstance.id,
      };
      this.beast.postBeastEvent(event);
      this.closeWidget('redline');

      let isConcurrencyFiling = this.instanceService?.currentInstance?.instanceMetadata?.attributes?.baselineVersion;
      let concurrencyDiff = sessionStorage.getItem('concurrencyDiff') === 'true';

      let { instanceData, navData } = getDeepestRouteSnapshot(this.route).data;

      if (value?.toggleType !== 'individual' && !isConcurrencyFiling) {
        this.RL.setRedlineData(this.RL.classifyDiffData(instanceData, navData));
      }
      // when user clicks on here link in concurrency popup - sourceVersion is 0 and targetVersion is latest - No need to set default values
      else if (value?.toggleType === 'all' && isConcurrencyFiling && concurrencyDiff) {
        localStorage.removeItem('concurrencyDiff');
        const draftVersionNumber = parseInt(
          this.instanceService?.currentInstance?.instanceMetadata?.attributes?.baselineVersion,
          10,
        );
        const draftVersion = (draftVersionNumber > 1 ? draftVersionNumber - 1 : 0).toString(10);

        console.log(draftVersion + ' : draft version');

        const newRedlineData: any = {};
        firstValueFrom(
          forkJoin([
            this.ES.findInstanceDifferencesUsingPOST(
              instanceData.instance.templateNamespace,
              instanceData.instance.templateName,
              {
                instanceParameters: {
                  targetInstanceId: instanceData.instance.id,
                  targetTemplateVersion: instanceData.instance.templateVersion,
                  sourceInstanceVersion: draftVersion,
                  targetInstanceVersion:
                    this.instanceService?.currentInstance?.instanceMetadata?.attributes?.baselineVersion,
                },
                options: {
                  changesOnly: true,
                  flattenResponse: true,
                },
              },
            ).pipe(
              map((a) => {
                return a?.differences?.collectedData || {};
              }),
            ),
            this.ES.findInstanceDifferencesUsingPOST(
              instanceData.instance.templateNamespace,
              instanceData.instance.templateName,
              {
                instanceParameters: {
                  targetInstanceId: instanceData.instance.id,
                  targetTemplateVersion: instanceData.instance.templateVersion,
                  sourceInstanceVersion: draftVersion,
                  targetInstanceVersion:
                    this.instanceService?.currentInstance?.instanceMetadata?.attributes?.baselineVersion,
                },
                options: {
                  changesOnly: false,
                  flattenResponse: false,
                },
              },
            ).pipe(
              map((a) => a?.differences?.collectedData || {}),
              map((diff) => {
                return JSON.parse(
                  JSON.stringify(diff, (key, value) => {
                    if (
                      value?.decafDiff?.op === 'update' &&
                      typeof value?.decafDiff?.source === 'string' &&
                      value?.decafDiff?.target === ''
                    ) {
                      value.decafDiff.op = 'delete';
                    }
                    if (
                      (value?.decafDiff && value?.decafDiff?.source instanceof Object) ||
                      value?.decafDiff?.target instanceof Object
                    ) {
                      const op =
                        value.decafDiff.op === 'update' && value.decafDiff.target === null
                          ? 'delete'
                          : value.decafDiff.op;
                      const diffValKey = op === 'insert' ? 'target' : op === 'delete' ? 'source' : '';
                      const spreadValues = Array.isArray(value.decafDiff[diffValKey]) ? [] : {};
                      for (const [k, v] of Object.entries(value.decafDiff[diffValKey] ?? {})) {
                        spreadValues[k] = {
                          decafDiff: {
                            op,
                            [diffValKey]: v,
                          },
                        };
                      }
                      return Array.isArray(spreadValues) ? spreadValues : { ...value, ...spreadValues };
                    }
                    return value;
                  }),
                );
              }),
              tap((diff) => {
                newRedlineData.diff = diff;
                newRedlineData.model = this.instanceService.getRedlineModel(diff);
              }),
            ),
          ]),
        ).then(([flattenedDiff, diff]) => {
          instanceData.redlineData.model = newRedlineData.model;
          this.RL.setRedlineData(
            this.RL.classifyDiffData(
              {
                instance: instanceData.instance,
                redlineData: {
                  diff,
                  flattenedDiff,
                  model: newRedlineData.model,
                },
              },
              navData,
              draftVersion,
            ),
          );
        });
      }
      // when user clicks on continue button in concurrency popup - sourceVersion is baselineVersion and targetVersion is latest
      else if (value?.toggleType === 'all' && isConcurrencyFiling && !concurrencyDiff) {
        const newRedlineData: any = {};
        firstValueFrom(
          forkJoin([
            this.ES.findInstanceDifferencesUsingPOST(
              instanceData.instance.templateNamespace,
              instanceData.instance.templateName,
              {
                instanceParameters: {
                  targetInstanceId: instanceData.instance.id,
                  targetTemplateVersion: instanceData.instance.templateVersion,
                  sourceInstanceVersion:
                    this.instanceService?.currentInstance?.instanceMetadata?.attributes?.baselineVersion,
                },
                options: {
                  changesOnly: true,
                  flattenResponse: true,
                },
              },
            ).pipe(
              map((a) => {
                return a?.differences?.collectedData || {};
              }),
            ),
            this.ES.findInstanceDifferencesUsingPOST(
              instanceData.instance.templateNamespace,
              instanceData.instance.templateName,
              {
                instanceParameters: {
                  targetInstanceId: instanceData.instance.id,
                  targetTemplateVersion: instanceData.instance.templateVersion,
                  sourceInstanceVersion:
                    this.instanceService?.currentInstance?.instanceMetadata?.attributes?.baselineVersion,
                },
                options: {
                  changesOnly: false,
                  flattenResponse: false,
                },
              },
            ).pipe(
              map((a) => a?.differences?.collectedData || {}),
              map((diff) => {
                return JSON.parse(
                  JSON.stringify(diff, (key, value) => {
                    if (
                      value?.decafDiff?.op === 'update' &&
                      typeof value?.decafDiff?.source === 'string' &&
                      value?.decafDiff?.target === ''
                    ) {
                      value.decafDiff.op = 'delete';
                    }
                    if (
                      (value?.decafDiff && value?.decafDiff?.source instanceof Object) ||
                      value?.decafDiff?.target instanceof Object
                    ) {
                      const op =
                        value.decafDiff.op === 'update' && value.decafDiff.target === null
                          ? 'delete'
                          : value.decafDiff.op;
                      const diffValKey = op === 'insert' ? 'target' : op === 'delete' ? 'source' : '';
                      const spreadValues = Array.isArray(value.decafDiff[diffValKey]) ? [] : {};
                      for (const [k, v] of Object.entries(value.decafDiff[diffValKey] ?? {})) {
                        spreadValues[k] = {
                          decafDiff: {
                            op,
                            [diffValKey]: v,
                          },
                        };
                      }
                      return Array.isArray(spreadValues) ? spreadValues : { ...value, ...spreadValues };
                    }
                    return value;
                  }),
                );
              }),
              tap((diff) => {
                newRedlineData.diff = diff;
                newRedlineData.model = this.instanceService.getRedlineModel(diff);
              }),
            ),
          ]),
        ).then(([flattenedDiff, diff]) => {
          instanceData.redlineData.model = newRedlineData.model;
          this.RL.setRedlineData(
            this.RL.classifyDiffData(
              {
                instance: instanceData.instance,
                redlineData: {
                  diff,
                  flattenedDiff,
                  model: newRedlineData.model,
                },
              },
              navData,
              instanceData.instance.instanceMetadata.attributes.baslineVersion,
            ),
          );
        });
      }
      // when user clicks on view individual changes only
      else {
        const newRedlineData: any = {};
        firstValueFrom(
          forkJoin([
            this.ES.findInstanceDifferencesUsingPOST(
              instanceData.instance.templateNamespace,
              instanceData.instance.templateName,
              {
                instanceParameters: {
                  targetInstanceId: instanceData.instance.id,
                  targetTemplateVersion: instanceData.instance.templateVersion,
                  sourceInstanceVersion:
                    instanceData.instance.instanceMetadata.attributes.filingStatus.attestationRequested.version,
                  targetInstanceVersion: instanceData.instance.instanceMetadata.attributes.filingStatus.attestedVersion,
                },
                options: {
                  changesOnly: true,
                  flattenResponse: true,
                },
              },
            ).pipe(
              map((a) => {
                return a?.differences?.collectedData || {};
              }),
            ),
            this.ES.findInstanceDifferencesUsingPOST(
              instanceData.instance.templateNamespace,
              instanceData.instance.templateName,
              {
                instanceParameters: {
                  targetInstanceId: instanceData.instance.id,
                  targetTemplateVersion: instanceData.instance.templateVersion,
                  sourceInstanceVersion:
                    instanceData.instance.instanceMetadata.attributes.filingStatus.attestationRequested.version,
                  targetInstanceVersion: instanceData.instance.instanceMetadata.attributes.filingStatus.attestedVersion,
                },
                options: {
                  changesOnly: false,
                  flattenResponse: false,
                },
              },
            ).pipe(
              map((a) => a?.differences?.collectedData || {}),
              map((diff) => {
                return JSON.parse(
                  JSON.stringify(diff, (key, value) => {
                    if (
                      value?.decafDiff?.op === 'update' &&
                      typeof value?.decafDiff?.source === 'string' &&
                      value?.decafDiff?.target === ''
                    ) {
                      value.decafDiff.op = 'delete';
                    }
                    if (
                      (value?.decafDiff && value?.decafDiff?.source instanceof Object) ||
                      value?.decafDiff?.target instanceof Object
                    ) {
                      const op =
                        value.decafDiff.op === 'update' && value.decafDiff.target === null
                          ? 'delete'
                          : value.decafDiff.op;
                      const diffValKey = op === 'insert' ? 'target' : op === 'delete' ? 'source' : '';
                      const spreadValues = Array.isArray(value.decafDiff[diffValKey]) ? [] : {};
                      for (const [k, v] of Object.entries(value.decafDiff[diffValKey] ?? {})) {
                        spreadValues[k] = {
                          decafDiff: {
                            op,
                            [diffValKey]: v,
                          },
                        };
                      }
                      return Array.isArray(spreadValues) ? spreadValues : { ...value, ...spreadValues };
                    }
                    return value;
                  }),
                );
              }),
              tap((diff) => {
                newRedlineData.diff = diff;
                newRedlineData.model = this.instanceService.getRedlineModel(diff);
              }),
            ),
          ]),
        ).then(([flattenedDiff, diff]) => {
          instanceData.redlineData.model = newRedlineData.model;
          this.RL.setRedlineData(
            this.RL.classifyDiffData(
              {
                instance: instanceData.instance,
                redlineData: {
                  diff,
                  flattenedDiff,
                  model: newRedlineData.model,
                },
              },
              navData,
              instanceData.instance.instanceMetadata.attributes.filingStatus.attestationRequested.version,
            ),
          );
        });
      }
    } else {
      this.DCS.resetToDefault();
      this.closeWidget('redline');
    }
    this.cd.markForCheck();
  }

  getVisuallySortedDisclosures(model: any[]): any[] {
    if (!model) return [];
    return [...model].sort((a, b) => {
      if (a.type === b.type) return 0;
      return a.type === 'question' ? -1 : 1;
    });
  }

  isDisclosureSelected(obj: any, model: any[]): boolean {
    const actualIndex = model?.findIndex((item) => item === obj);
    return actualIndex === this.selectedDisclosureIndex;
  }

  onSelectDisclosure(data, index: number, isContinueDisabledSubject: BehaviorSubject<boolean>, model?: any[]) {
    // Find the actual index in the original model based on disclosureExplanationNumber
    const actualIndex = model?.findIndex((item) => item === data) ?? index;

    // Clear delete reason from previously selected item only
    if (model && this.selectedDisclosureIndex !== null && this.selectedDisclosureIndex !== actualIndex) {
      const previousItem = model[this.selectedDisclosureIndex];
      if (previousItem && previousItem.disclosureExplanationDeleteReason) {
        delete previousItem.disclosureExplanationDeleteReason;
      }
    }

    this.modalDeletionReason = '';
    this.disclosureDeleteReasonError = '';

    this.selectedDisclosureIndex = actualIndex;
    this.currentSelectDisclosure = data;

    const isNewStatus = data?.disclosureExplanationStatus === 'CREATE';
    const specialChars = /^((?!.*<(?! *>).*>)(?! +$|\r?\n+$).+|\r?\n)*$/;
    const existingReason = data?.disclosureExplanationDeleteReason || '';
    const hasValidReason = existingReason && existingReason.trim().length > 0 && specialChars.test(existingReason);

    isContinueDisabledSubject.next(!isNewStatus && !hasValidReason);
  }

  shouldShowTypeSeparator(model: any[], index: number, currentType: string): boolean {
    if (index === 0) return true;

    // Find the last visible item before current index
    for (let i = index - 1; i >= 0; i--) {
      const item = model[i];
      if (!(item?.associations?.length > 0 || item?.disclosureExplanationStatus === 'REMOVE')) {
        return item.type !== currentType;
      }
    }

    return true; // No previous visible item found
  }

  shouldHideAllDisclosures(model: any[]): boolean {
    return (
      model?.every((obj) => obj?.associations?.length > 0 || obj?.disclosureExplanationStatus === 'REMOVE') || false
    );
  }

  getEnumLabel(item, key): string {
    let label = '';
    if (this.CC.formSelector?.dispositionTypeOptions?.length > 0) {
      this.CC.formSelector.dispositionTypeOptions.forEach((enumType) => {
        if (enumType.value === item[key]) {
          label = enumType.label;
        }
      });
    }
    if (this.CC.formSelector?.disclosureExplanationStatusOptions?.length > 0) {
      this.CC.formSelector.disclosureExplanationStatusOptions.forEach((enumType) => {
        if (enumType.value === item[key]) {
          label = enumType.label;
        }
      });
    }
    return label;
  }
}
